import dayjs from "dayjs";
import {
    addPartialDataZones,
    isPartialDataPoint,
} from "app/UtilitiesAndConstants/UtilityFunctions/monthsToDateUtilityFunctions";
import type {
    DataGranularity,
    ICategoryShareServices,
    ICategoryShareTableSelection,
    WebSourceType,
} from "../../CategoryShareTypes";
import type {
    ICategoryShareAdaptedGraphData,
    ICategoryShareGraphApiData,
    WebSourceToDataMapping } from "../CategoryShareGraphTypes";
import {
    DataTypeSwitcherEnum
} from "../CategoryShareGraphTypes";
import ChartMarkerService from "app/services/ChartMarkerService";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import { resolveLastSupportedDate, resolveWebSourceForGraphApi } from "./CategoryShareGraphUtils";
import type { ISegmentsModule } from "app/services/segments/segmentsApiService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
interface IDataAdaptProps {
    graphApiData: ICategoryShareGraphApiData;
    websource: WebSourceType;
    dataGranularity: DataGranularity;
    duration: string;
    selectedDataType: DataTypeSwitcherEnum;
    selectedTableRows: ICategoryShareTableSelection[];
    hiddenDomains: string[];
    services: ICategoryShareServices;
    shouldAddOthersRecord: boolean;
    isMonthToDateEnabled: boolean;
}

export const adaptGraphData = (
    props: IDataAdaptProps,
    segmentsModule: ISegmentsModule,
): ICategoryShareAdaptedGraphData[] => {
    const {
        graphApiData,
        selectedTableRows,
        websource,
        selectedDataType,
        hiddenDomains,
        dataGranularity,
        services,
        duration,
        shouldAddOthersRecord,
        isMonthToDateEnabled,
    } = props;

    const hasTableData = selectedTableRows && selectedTableRows.length > 0;
    const hasGraphData = graphApiData?.Data && Object.entries(graphApiData.Data).length > 0;
    if (!hasTableData || !hasGraphData) {return [];}
    const getUserAndAccountSegmentById = SegmentsUtils.getUserAndAccountSegmentByIdHof(segmentsModule);
    const lastSupportedDate = resolveLastSupportedDate(duration, isMonthToDateEnabled, services);

    const data: ICategoryShareAdaptedGraphData[] = Object.entries(graphApiData.Data)
        .filter((domain) => {
            const [id] = domain;
            return isDomainSelectedOnTable(id, selectedTableRows);
        })
        .filter((domain) => {
            const [, domainData] = domain;
            return hasDomainData(domainData, websource);
        })
        .sort((thisDomain, otherDomain) => {
            const [thisDomainName] = thisDomain;
            const [otherDomainName] = otherDomain;
            return sortAccordingToTableRows(thisDomainName, otherDomainName, selectedTableRows);
        })
        .map((domain, index) => {
            const [id, domainData] = domain;
            const segment = getUserAndAccountSegmentById(id);
            const domainName = segment ? segment.domain : id;
            const domainRowInTable = selectedTableRows.find((row) => row.Id === id);
            const { selectionColor } = domainRowInTable;

            const domainDataPoints = adaptDataPointsForDomain(
                domainData,
                websource,
                dataGranularity,
                selectedDataType,
                lastSupportedDate,
                isMonthToDateEnabled,
            );

            return {
                name: domainName,
                id,
                seriesName: domainName,
                showInLegend: false,
                color: selectionColor,
                data: domainDataPoints,
                index: selectedTableRows.length - index,
                yAxis: 0,
                zIndex: 1,
                marker: {
                    symbol: ChartMarkerService.createMarkerStyle(selectionColor).background,
                },
                visible: !hiddenDomains.includes(id),
                zoneAxis: null,
                zones: null,
                ...(segment && { subtitle: segment.segmentName }),
            };
        });

    if (shouldAddOthersRecord) {
        const othersRecord = createOthersRecord(
            data,
            dataGranularity,
            lastSupportedDate,
            hiddenDomains,
            isMonthToDateEnabled,
        );
        data.push(othersRecord);
    }

    const result = addPartialDataZones(data, {
        chartType: chartTypes.AREA,
    });

    return result;
};

const isDomainSelectedOnTable = (id: string, selectedTableRows: ICategoryShareTableSelection[]) => {
    const isDomainSelected = selectedTableRows.find((row) => row.Id === id);
    return isDomainSelected;
};

const hasDomainData = (domainData: WebSourceToDataMapping, websource: WebSourceType) => {
    const adaptedWebSource = resolveWebSourceForGraphApi(websource);
    const domainDataPoints = domainData?.[adaptedWebSource] ?? [];
    const hasData = domainDataPoints.length > 0;
    return hasData;
};

const sortAccordingToTableRows = (
    thisDomainName: string,
    otherDomainName: string,
    selectedTableRows: ICategoryShareTableSelection[],
) => {
    const thisDomainRowInTable = selectedTableRows?.find((row) => row.Domain === thisDomainName)?.index ?? -1;

    const otherDomainRowInTable = selectedTableRows?.find((row) => row.Domain === otherDomainName)?.index ?? -1;

    return thisDomainRowInTable > otherDomainRowInTable ? 1 : -1;
};

const adaptDataPointsForDomain = (
    domainData: WebSourceToDataMapping,
    websource: WebSourceType,
    dataGranularity: DataGranularity,
    selectedDataType: DataTypeSwitcherEnum,
    lastSupportedDate: string,
    isMonthToDateEnabled: boolean,
): Array<{ x: number; y: number; isPartial: boolean }> => {
    const adaptedWebSource = resolveWebSourceForGraphApi(websource);
    const domainDataPoints = domainData[adaptedWebSource].map((point, pointIndex, domainPoints) => {
        const pointDate = dayjs.utc(point.Key).valueOf();
        const pointValue = selectedDataType === DataTypeSwitcherEnum.PERCENT ? point.Value.Share : point.Value.Absolute;

        return {
            x: pointDate,
            y: pointValue,
            isPartial: isMonthToDateEnabled
                ? isPartialDataPoint(pointIndex, domainPoints, point, dataGranularity, lastSupportedDate)
                : false,
        };
    });
    return domainDataPoints;
};

const createOthersRecord = (
    graphData: ICategoryShareAdaptedGraphData[],
    dataGranularity: DataGranularity,
    lastSupportedDate: string,
    hiddenDomains: string[],
    isMonthToDateEnabled: boolean,
): ICategoryShareAdaptedGraphData => {
    const OTHERS_DOMAIN_NAME = "Others";
    const dateToValueSumMapping = createDateToValueSumMapping(graphData);
    const othersRecord: ICategoryShareAdaptedGraphData = {
        name: OTHERS_DOMAIN_NAME,
        id: OTHERS_DOMAIN_NAME,
        showInLegend: false,
        color: "#E6E6E6",
        seriesName: "Others",
        data: graphData[0].data.map((point, index, allPoints) => ({
            x: point.x,
            y: 1 - dateToValueSumMapping[point.x],
            isPartial: isMonthToDateEnabled
                ? isPartialDataPoint(index, allPoints, { Key: point.x }, dataGranularity, lastSupportedDate)
                : false,
        })),
        index: 0,
        yAxis: 0,
        zIndex: 1,
        marker: {
            symbol: ChartMarkerService.createMarkerStyle("#E6E6E6").background,
        },
        visible: !hiddenDomains.includes("Others"),
        zones: null,
        zoneAxis: null,
    };

    return othersRecord;
};

const createDateToValueSumMapping = (graphData: ICategoryShareAdaptedGraphData[]) => {
    const flattenedGraphData = flattenGraphDataPoints(graphData);

    const dateToValueMapping: Record<number, number> = flattenedGraphData.reduce((mapResult, graphPoint) => {
        mapResult.hasOwnProperty(graphPoint.date)
            ? (mapResult[graphPoint.date] += graphPoint.value)
            : (mapResult[graphPoint.date] = graphPoint.value);
        return mapResult;
    }, {});

    return dateToValueMapping;
};

const flattenGraphDataPoints = (graphData: ICategoryShareAdaptedGraphData[]) => {
    const graphDataPoints = graphData
        .filter((domain) => domain.visible)
        .reduce((res, domain) => {
            const domainDataPoints = domain.data.map((point) => {
                return {
                    date: point.x,
                    value: point.y,
                };
            });

            return [...res, ...domainDataPoints];
        }, [] as Array<{ date: number; value: number }>);

    return graphDataPoints;
};
